import React, { useMemo } from "react";
import "./Zone.css";
import { normalizeWard } from "../../Utils/ward";

function wardStatusColor(completionPct) {
  if (completionPct >= 80) return "#3d7a50";
  if (completionPct >= 60) return "#7a6030";
  return "#7a3030";
}

function Zone({ className, zone, locationsData, onBack, onWardClick }) {
  const wards = useMemo(() => {
    if (!locationsData || !zone) return [];
    const map = {};
    Object.values(locationsData)
      .filter((l) => String(l.zone) === String(zone.id))
      .forEach((loc) => {
        // Ward codes are entered inconsistently ("52", "NO52", "N052", ...);
        // normalize so the same ward doesn't split into separate rows.
        const w = normalizeWard(loc.ward);
        if (w == null) return;
        if (!map[w]) map[w] = { id: w, total: 0, active: 0, takenOver: 0, takenOverAndActive: 0, openComplaints: 0 };
        map[w].total += 1;
        if (loc.operationalStatus?.state === "Active") map[w].active += 1;
        if (loc.takeoverTime != null) map[w].takenOver += 1;
        if (loc.takeoverTime != null && loc.operationalStatus?.state === "Active") map[w].takenOverAndActive += 1;
        map[w].openComplaints += loc.totalOpenCompliant || 0;
      });
    return Object.entries(map)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([id, d]) => ({
        id,
        total: d.total,
        active: d.active,
        takenOver: d.takenOver,
        takenOverAndActive: d.takenOverAndActive,
        completionPct: d.total > 0 ? Math.round((d.active / d.total) * 100) : 0,
        openComplaints: d.openComplaints,
      }));
  }, [locationsData, zone]);

  if (!zone) return null;

  const operational = zone.takenOver || 0;
  const inProgress = zone.active || 0;
  const pending = (zone.total || 0) - (zone.takenOverAndActive || 0);

  return (
    <div className={className} data-cy="zone-screen">
      <div className="subhdr">
        <div className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </div>
        <div className="subhdr-info">
          <div className="subhdr-crumb">Chennai</div>
          <div className="subhdr-title">Zone {zone.id}</div>
        </div>
      </div>
      <div className="divider" />
      <div className="scroll">
        <div style={{ padding: "6px 20px 10px", fontSize: "var(--text-base)", color: "var(--t2)" }}>
          {zone.total} PCTs · {wards.length} wards
        </div>
        <div className="stats c3" style={{ marginBottom: "16px" }}>
          <div className="stat">
            <div className="stat-n" style={{ color: "#5fbe7a" }}>{operational}</div>
            <div className="stat-l">Taken Over</div>
          </div>
          <div className="stat">
            <div className="stat-n" style={{ color: "#c39a52" }}>{inProgress}</div>
            <div className="stat-l">Active</div>
          </div>
          <div className="stat">
            <div className="stat-n" style={{ color: "var(--t2)" }}>{pending}</div>
            <div className="stat-l">Pending</div>
          </div>
        </div>
        <div className="sec-hdr">
          <div className="t-section">Wards</div>
          <div className="t-label">{wards.length} wards</div>
        </div>
        <div className="lrows">
          {wards.map((ward) => {
            const color = wardStatusColor(ward.completionPct);
            return (
              <div
                key={ward.id}
                className="lrow"
                onClick={() => onWardClick && onWardClick(ward)}
                data-cy={`ward-row-${ward.id}`}
              >
                <div className="licon">{ward.id}</div>
                <div className="li">
                  <div className="ln">Ward {ward.id}</div>
                  <div className="ls">{ward.total} PCTs</div>
                </div>
                <div className="lr">
                  <div className="lpct">{ward.completionPct}%</div>
                  <div className="ll">complete</div>
                  <div className="mbar">
                    <div className="mfill" style={{ width: `${ward.completionPct}%`, background: color }} />
                  </div>
                </div>
                <div className="chevron">
                  <svg viewBox="0 0 24 24">
                    <polyline points="9,18 15,12 9,6" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ height: "20px" }} />
      </div>
    </div>
  );
}

export default Zone;
